import { useState, type ReactElement } from 'react';
import { api, type ResolutionQueueItem } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { formatDateTime, formatMinutes } from '../shared/lib/format';
import { useAsync } from '../shared/lib/use-async';
import {
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  OrderNumber,
  StatusChip,
  dangerButtonStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../shared/ui/primitives';

/**
 * Очередь разбора недоставок (разд. 8, FR-574).
 *
 * Список карточек, а не таблица: по каждой недоставке диспетчеру нужно сразу видеть
 * контекст — причину от водителя, время ожидания, обращения ИИ-контура, — а не
 * открывать записи по одной.
 *
 * Заказ, висящий дольше часа, помечен `danger` (FR-576): решение обязательно, заказ не
 * может оставаться в `not_delivered` бессрочно.
 */

const ALERT_MINUTES = 60;
const REFRESH_MS = 30_000;

export function ResolutionsPage(): ReactElement {
  const messages = useMessages();
  const queue = useAsync(() => api.resolutionQueue(), [], { intervalMs: REFRESH_MS });
  // Причина — словами из справочника на языке интерфейса: `recipient_absent` логисту
  // ничего не говорит. Справочник без кода — показывается сам код.
  const reasons = useAsync(() => api.reasonCodes('not_delivered'), []);
  const reasonName = (code: string | null | undefined): string =>
    code === null || code === undefined
      ? ''
      : ((reasons.data ?? []).find((reason) => reason.code === code)?.name ?? code);
  const [decision, setDecision] = useState<null | {
    item: ResolutionQueueItem;
    kind: 'cancelled' | 'rescheduled' | 'reassigned' | 'return';
  }>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const apply = async (): Promise<void> => {
    if (decision === null) {
      return;
    }

    try {
      if (decision.kind === 'return') {
        await api.returnToCt(decision.item.order.id, reason);
      } else {
        await api.resolve(decision.item.order.id, { decision: decision.kind, reason });
      }

      setDecision(null);
      setReason('');
      queue.reload();
    } catch (cause) {
      setError(String(cause));
    }
  };

  const items = queue.data?.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <h1>{messages.cabinet.resolutions.title}</h1>

      {error === null ? null : <div style={{ color: 'var(--danger)' }}>{error}</div>}

      {items.length === 0 ? (
        <EmptyState text={messages.cabinet.resolutions.empty} />
      ) : (
        items.map((item) => (
          <Card
            key={item.order.id}
            tone={item.waiting_minutes >= ALERT_MINUTES ? 'danger' : 'warn'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
              <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
                <OrderNumber
                  externalNumber={item.order.external_number}
                  displayId={item.order.display_id}
                />
                <StatusChip status={item.order.status} goodsLocation={item.order.goods_location} />
              </div>

              <span className="num" style={{ color: 'var(--text-muted)' }}>
                {`${messages.cabinet.resolutions.waiting} ${formatMinutes(item.waiting_minutes, { minutes: messages.common.minutesShort, hours: messages.common.hoursShort })}`}
              </span>
            </div>

            <Field label={messages.cabinet.create.address} value={item.order.address.raw} />

            <Field
              label={messages.cabinet.resolutions.reason}
              value={
                item.attempt === null
                  ? messages.cabinet.common.empty
                  : [reasonName(item.attempt.reason_code), item.attempt.comment ?? '']
                      .filter((part) => part !== '')
                      .join(' · ')
              }
            />

            <div style={{ display: 'flex', gap: 'var(--sp-4)', color: 'var(--text-muted)' }}>
              <span>
                {`${messages.cabinet.order.ctMessages}: `}
                <span className="num">{item.ct_messages_count}</span>
              </span>
              <span>
                {`${messages.cabinet.order.recipient}: ${item.order.recipient_name ?? ''} ${item.order.recipient_phone}`}
              </span>
            </div>

            {/*
              Решение, уже принятое ИИ-контуром, показывается явно: диспетчер не должен
              дублировать работу и звонить получателю второй раз (разд. 13 ТЗ, риск
              двойной коммуникации).
            */}
            {item.existing_resolution === undefined ? null : (
              <div style={{ color: 'var(--wait)' }}>
                {`${messages.cabinet.resolutions.ctHandled} · ${formatDateTime(
                  item.existing_resolution.resolved_at,
                )}`}
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
              <button
                type="button"
                style={primaryButtonStyle}
                onClick={() => setDecision({ item, kind: 'reassigned' })}
              >
                {messages.cabinet.resolutions.decisionReassign}
              </button>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={() => setDecision({ item, kind: 'rescheduled' })}
              >
                {messages.cabinet.resolutions.decisionReschedule}
              </button>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={() => setDecision({ item, kind: 'return' })}
              >
                {messages.cabinet.resolutions.returnToCt}
              </button>
              <button
                type="button"
                style={dangerButtonStyle}
                onClick={() => setDecision({ item, kind: 'cancelled' })}
              >
                {messages.cabinet.resolutions.decisionCancel}
              </button>
            </div>
          </Card>
        ))
      )}

      {decision === null ? null : (
        <ConfirmDialog
          title={messages.cabinet.resolutions.title}
          description={decision.item.order.address.raw}
          requireReason
          reason={reason}
          onReason={setReason}
          onCancel={() => setDecision(null)}
          onConfirm={() => void apply()}
        />
      )}
    </div>
  );
}
